import { createContext, useContext } from "react";
import type {
    ExpansionState,
    FlatItem,
    GroupedTableColumn,
    GroupedTableInternalContextValue,
    GroupedTableSortDirection,
    GroupedTableSortState,
    GroupedTableState,
    GroupId,
    GroupNode,
    GroupPath,
    RowEntity,
    RowId,
} from "./types";

export const ROOT_ID: GroupId = "__grouped_table_root__";

export type GroupedTableViewBuildOptions<TRow> = {
    columns: readonly GroupedTableColumn<TRow>[];
    sort?: GroupedTableSortState<TRow> | undefined;
    searchText?: string | undefined;
    applyLocalSort?: boolean | undefined;
    applyLocalFilter?: boolean | undefined;
};

export type GroupedTableViewBuildResult = {
    flatItems: FlatItem[];
};

export function encodePart(x: unknown): string {
    return encodeURIComponent(String(x ?? "__NULL__"));
}

export function defaultMakeGroupId(path: GroupPath): GroupId {
    if (path.length === 0) {
        return ROOT_ID;
    }

    return path
        .map((p) => `${encodePart(p.fieldId)}=${encodePart(p.value)}`)
        .join("|");
}

export function isGroupExpanded<TRow>(
    group: GroupNode<TRow>,
    exp: ExpansionState,
    rootId: GroupId,
): boolean {
    if (group.id === rootId) {
        return true;
    }

    if (exp.collapsed[group.id]) {
        return false;
    }

    if (exp.expanded[group.id]) {
        return true;
    }

    return group.depth < exp.globalExpandedDepth;
}

export function toMap(ids: GroupId[] | undefined): Record<GroupId, boolean> {
    const out: Record<GroupId, boolean> = {};

    for (const id of ids ?? []) {
        out[id] = true;
    }

    return out;
}

function isDateValue(value: unknown): value is Date {
    return value instanceof Date && !Number.isNaN(value.getTime());
}

function comparePrimitiveValues(a: unknown, b: unknown): number {
    if (a === b) {
        return 0;
    }

    if (a === undefined || a === null) {
        return 1;
    }

    if (b === undefined || b === null) {
        return -1;
    }

    if (typeof a === "number" && typeof b === "number") {
        return a - b;
    }

    if (isDateValue(a) && isDateValue(b)) {
        return a.getTime() - b.getTime();
    }

    if (typeof a === "boolean" && typeof b === "boolean") {
        return Number(a) - Number(b);
    }

    return String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: "base",
    });
}

export function compareValues(
    a: unknown,
    b: unknown,
    direction: GroupedTableSortDirection,
    comparator?: ((left: unknown, right: unknown) => number) | undefined,
): number {
    const baseResult = comparator
        ? comparator(a, b)
        : comparePrimitiveValues(a, b);

    return direction === "desc"
        ? -baseResult
        : baseResult;
}

function getColumnById<TRow>(
    columns: readonly GroupedTableColumn<TRow>[],
    columnId: keyof TRow,
): GroupedTableColumn<TRow> | undefined {
    return columns.find((column) => column.id === columnId);
}

export function getRowSortValue<TRow>(
    row: RowEntity<TRow>,
    column: GroupedTableColumn<TRow>,
): unknown {
    if (column.sortAccessor) {
        return column.sortAccessor(row.data);
    }

    return row.data[column.id];
}

export function getGroupSortValue<TRow>(
    group: GroupNode<TRow>,
    column: GroupedTableColumn<TRow>,
): unknown {
    const groupValue = group.values?.[column.id];
    if (groupValue !== undefined) {
        return groupValue;
    }

    if (column.groupSortAccessor) {
        return column.groupSortAccessor(group);
    }

    return group.label;
}

function normalizeSearchValue(value: string | string[]): string[] {
    return (Array.isArray(value) ? value : [value])
        .map((item) => item.trim().toLocaleLowerCase())
        .filter(Boolean);
}

function getDefaultSearchValue(value: unknown): string[] {
    if (value === undefined || value === null) {
        return [];
    }

    if (Array.isArray(value)) {
        return value
            .map((item) => String(item).trim().toLocaleLowerCase())
            .filter(Boolean);
    }

    return [String(value).trim().toLocaleLowerCase()].filter(Boolean);
}

export function getRowSearchValues<TRow>(
    row: RowEntity<TRow>,
    columns: readonly GroupedTableColumn<TRow>[],
): string[] {
    return columns
        .filter((column) => column.searchable !== false)
        .flatMap((column) => {
            if (column.searchAccessor) {
                return normalizeSearchValue(column.searchAccessor(row.data));
            }

            return getDefaultSearchValue(row.data[column.id]);
        });
}

export function getGroupSearchValues<TRow>(
    group: GroupNode<TRow>,
    columns: readonly GroupedTableColumn<TRow>[],
): string[] {
    return columns
        .filter((column) => column.searchable !== false)
        .flatMap((column, index) => {
            if (column.groupSearchAccessor) {
                return normalizeSearchValue(column.groupSearchAccessor(group));
            }

            const groupValue = group.values?.[column.id];
            if (groupValue !== undefined && groupValue !== null) {
                return getDefaultSearchValue(groupValue);
            }

            if (index === 0) {
                return getDefaultSearchValue(group.label);
            }

            return [];
        });
}

function matchesSearch(values: string[], normalizedSearchText: string): boolean {
    if (!normalizedSearchText) {
        return true;
    }

    return values.some((value) => value.includes(normalizedSearchText));
}

function getSortedChildGroupIds<TRow>(
    group: GroupNode<TRow>,
    state: GroupedTableState<TRow>,
    column: GroupedTableColumn<TRow>,
    direction: GroupedTableSortDirection,
): GroupId[] {
    return [...group.childGroupIds].sort((leftId, rightId) => {
        const left = state.groups[leftId];
        const right = state.groups[rightId];

        if (!left || !right) {
            return 0;
        }

        return compareValues(
            getGroupSortValue(left, column),
            getGroupSortValue(right, column),
            direction,
            column.sortComparator,
        );
    });
}

function getSortedRowIds<TRow>(
    group: GroupNode<TRow>,
    state: GroupedTableState<TRow>,
    column: GroupedTableColumn<TRow>,
    direction: GroupedTableSortDirection,
): RowId[] {
    return [...group.rowIds].sort((leftId, rightId) => {
        const left = state.rows[leftId];
        const right = state.rows[rightId];

        if (!left || !right) {
            return 0;
        }

        return compareValues(
            getRowSortValue(left, column),
            getRowSortValue(right, column),
            direction,
            column.sortComparator,
        );
    });
}

type TraverseResult = {
    include: boolean;
    items: FlatItem[];
};

function buildGroupKey(path: GroupPath): string {
    return defaultMakeGroupId(path).toString();
}

function buildRootTerminalItem<TRow>(root: GroupNode<TRow>): FlatItem | null {
    if (root.status === "not-loaded" || root.status === "loading") {
        return { kind: "loading", parentGroupId: root.id, key: "root-loading" };
    }

    if (root.status === "error") {
        return {
            kind: "error",
            parentGroupId: root.id,
            message: root.error || "Unknown error",
            key: "root-error",
        };
    }

    return null;
}

export function buildViewFlatItems<TRow>(
    state: GroupedTableState<TRow>,
    options: GroupedTableViewBuildOptions<TRow>,
): GroupedTableViewBuildResult {
    const items: FlatItem[] = [];
    const { groups, rootId } = state;
    const root = groups[rootId];

    if (!root) {
        return { flatItems: items };
    }

    const terminalItem = buildRootTerminalItem(root);
    if (terminalItem) {
        return { flatItems: [terminalItem] };
    }

    const normalizedSearchText = (options.searchText ?? "").trim().toLocaleLowerCase();
    const applyLocalFilter = !!options.applyLocalFilter && normalizedSearchText.length > 0;
    const activeSort = options.applyLocalSort
        ? options.sort
        : undefined;
    const activeSortColumn = !activeSort
        ? undefined
        : getColumnById(options.columns, activeSort.columnId);

    function getGroupIds(group: GroupNode<TRow>): GroupId[] {
        if (!activeSort || !activeSortColumn) {
            return group.childGroupIds;
        }

        return getSortedChildGroupIds(group, state, activeSortColumn, activeSort.direction);
    }

    function getRowIds(group: GroupNode<TRow>): RowId[] {
        if (!activeSort || !activeSortColumn) {
            return group.rowIds;
        }

        return getSortedRowIds(group, state, activeSortColumn, activeSort.direction);
    }

    function traverseGroup(groupId: GroupId, ancestorMatched = false): TraverseResult {
        const group = groups[groupId];
        if (!group) {
            return { include: false, items: [] };
        }

        const groupKey = buildGroupKey(group.path);
        const expanded = isGroupExpanded(group, state.expansion, rootId);
        const groupMatches = applyLocalFilter
            ? matchesSearch(getGroupSearchValues(group, options.columns), normalizedSearchText)
            : false;
        const branchMatched = ancestorMatched || groupMatches;
        const childItems: FlatItem[] = [];
        let hasMatchingDescendants = false;

        if (group.status === "loading" || group.status === "not-loaded") {
            hasMatchingDescendants = branchMatched;

            if (expanded && branchMatched) {
                childItems.push({
                    kind: "loading",
                    parentGroupId: group.id,
                    key: `${groupKey}|loading`,
                });
            }
        } else if (group.status === "error") {
            hasMatchingDescendants = branchMatched;

            if (expanded && branchMatched) {
                childItems.push({
                    kind: "error",
                    parentGroupId: group.id,
                    message: group.error || "Unknown error",
                    key: `${groupKey}|error`,
                });
            }
        } else {
            for (const childGroupId of getGroupIds(group)) {
                const childResult = traverseGroup(childGroupId, branchMatched);
                if (!childResult.include) {
                    continue;
                }

                hasMatchingDescendants = true;

                if (expanded) {
                    childItems.push(...childResult.items);
                }
            }

            for (const rowId of getRowIds(group)) {
                const row = state.rows[rowId];
                if (!row) {
                    continue;
                }

                const rowMatches = branchMatched || !applyLocalFilter || matchesSearch(
                    getRowSearchValues(row, options.columns),
                    normalizedSearchText,
                );

                if (!rowMatches) {
                    continue;
                }

                hasMatchingDescendants = true;

                if (expanded) {
                    childItems.push({
                        kind: "row",
                        id: row.id,
                        parentGroupId: group.id,
                        key: `${groupKey}|row=${encodePart(row.id)}`,
                    });
                }
            }
        }

        if (!applyLocalFilter) {
            return {
                include: true,
                items: [
                    { kind: "group", id: group.id, key: groupKey },
                    ...childItems,
                ],
            };
        }

        const include = branchMatched || hasMatchingDescendants;

        if (!include) {
            return { include: false, items: [] };
        }

        return {
            include: true,
            items: [
                { kind: "group", id: group.id, key: groupKey },
                ...childItems,
            ],
        };
    }

    const rootRowIds = getRowIds(root);
    for (const rowId of rootRowIds) {
        const row = state.rows[rowId];
        if (!row) {
            continue;
        }

        if (applyLocalFilter && !matchesSearch(getRowSearchValues(row, options.columns), normalizedSearchText)) {
            continue;
        }

        items.push({
            kind: "row",
            id: row.id,
            parentGroupId: root.id,
            key: `${buildGroupKey(root.path)}|row=${encodePart(row.id)}`,
        });
    }

    for (const childGroupId of getGroupIds(root)) {
        const childResult = traverseGroup(childGroupId);
        if (childResult.include) {
            items.push(...childResult.items);
        }
    }

    return { flatItems: items };
}

export function buildFlatItems<TRow>(
    state: GroupedTableState<TRow>,
): FlatItem[] {
    return buildViewFlatItems(state, {
        columns: [],
    }).flatItems;
}

export const GroupedTableInternalContext = createContext<unknown>(null);
export function useGroupedTableInternal<TRow>(): GroupedTableInternalContextValue<TRow> {
    const ctx = useContext(GroupedTableInternalContext);
    if (!ctx) {
        throw new Error("useGroupedTableInternal must be used within a GroupedTableProvider");
    }

    return ctx as GroupedTableInternalContextValue<TRow>;
}
